import React, { useEffect, useState, useRef } from "react";
import { useFormik, FormikProvider, FieldArray } from "formik";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Spinner from "../../components/Spinner/spinner";
import { Toast } from "primereact/toast";
import { getTimesheetById } from "../../services/viewTimeSheet";
import { approveTimesheet, rejectTimesheet } from "../../services/timeSheetApproval";
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tooltip } from "primereact/tooltip";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "./addTimeSheet.css";
import "../../global.css";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { appInsights } from "../../AppInsights";

const ApprovalTimeSheet = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const toast = useRef(null);
    const [loading, setLoading] = useState(true);
    const [timesheetData, setTimesheetData] = useState(null);
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { userData } = useSelector(state => state?.user);
    const userType = localStorage.getItem('userType');
    const userTypeRole = userData?.userTypes?.find(type =>
    type.userType.toLowerCase().includes(userType?.toLowerCase())
);
const isSuperAdmin = userTypeRole?.userTypeRoleId === 1;
const isHROrAccounts = [3].includes(userTypeRole?.userTypeRoleId);

    const showToast = (severity, summary, detail) => {
        if (toast?.current) {
            toast?.current?.show({
                severity,
                summary,
                detail,
                life: 3000,
                closable: true
            });
        }
    };

    const fetchTimesheet = async () => {
        try {
            setLoading(true);
            const response = await getTimesheetById(id);
            setTimesheetData(response);

            // Group entries by date
            const groupedEntries = response?.entries?.reduce((acc, entry) => {
                const date = entry?.entryDate;
                if (!acc[date]) {
                    acc[date] = {
                        date,
                        projects: []
                    };
                }
                acc[date].projects.push({
                    projectName: entry?.projectName,
                    hours: entry?.hours,
                    comments: entry?.comments
                });
                return acc;
            }, {});

            // Convert grouped entries to array
            const entriesArray = Object.values(groupedEntries);
            formik.setFieldValue('entries', entriesArray);
        } catch (error) {
            appInsights.trackException("Get approvalTimeSheet", { exception: error });

            showToast('error', 'Error', error?.status==404?'Timesheet not found.':error?.status==500?"Internal Server Error":"Error occurred while fetching Details");
            // setTimeout(() => {
            //     navigate("/user/timesheet")
            // }, 2000);
            console.error('Error fetching timesheet:', error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (id) {
            fetchTimesheet();
        }
    }, [id]);

    const handleApprove = async () => {
        try {
            setSubmitting(true);
            await approveTimesheet({
                timeSheetId: id,
                statusId: 2 // Approved status
            });
            showToast('success', 'Success', 'Timesheet approved successfully');
            await fetchTimesheet();
            handleBack();
        } catch (error) {
            showToast('error', 'Error', error?.message || "Something went wrong at Server Side");
            appInsights.trackException("Post approvalTimeSheet", { exception: error });
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!rejectReason?.trim()) {
            showToast('error', 'Error', 'Please provide a reason for rejection');
            return;
        }

        try {
            setSubmitting(true);
            await rejectTimesheet({
                timeSheetId: id,
                statusId: 3, // Rejected status
                reason: rejectReason
            });
            showToast('success', 'Success', 'Timesheet rejected successfully');
            setShowRejectDialog(false);
            handleBack();
            await fetchTimesheet();
        } catch (error) {
            showToast('error', 'Error', error?.message || "Something went wrong at Server Side");
            appInsights.trackException("Post approvalTimeSheet", { exception: error });
        } finally {
            setSubmitting(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            entries: [],
        },
        onSubmit: () => { },
    });

    const formatDateForDisplay = (date) => {
        return date?format(new Date(date), 'EEE, MMM d'):"N/A";
    };

    const handleDownload = () => {
        if (timesheetData?.blobPath) {
            window.open(timesheetData.blobPath, '_blank');
        } else {
            showToast('error', 'Error', 'No document available for download');
        }
    };
    const isUserViewingOwnTimesheet = userData?.userName?.toLowerCase() === timesheetData?.userName?.toLowerCase();

    const handleBack = () => {
        const userTypeRole = userData?.userTypes?.find(type =>
          type?.userType?.toLowerCase().includes(userType?.toLowerCase())
        );
        
        if (userTypeRole?.userTypeRoleId === 1) {
          setTimeout(()=>navigate("/admin/timesheet-list"),500);
        }else if ([2].includes(userTypeRole?.userTypeRoleId)) {  
            setTimeout(()=>navigate("/hr/employees/timesheets"),500);
        } 
        else if ([3, 4].includes(userTypeRole?.userTypeRoleId)) {
          setTimeout(()=>navigate(-1),500);
        } else {
          setTimeout(()=>navigate("/user/timesheet"),500);
        }
      };

    return (
        <div className="timesheet-container p-3">
            <Toast ref={toast} />
            <Tooltip target="#download_btn" />
            {loading ? (
                <Spinner />
            ) : (
                <div className="card">
                    <Button
                     label="Back to list"
                     icon="pi pi-arrow-left"
                     className="p-button-text p-0 mb-1 back_btn"
                     onClick={handleBack}
                     disabled={submitting}
                    />
                    <div className="md:flex justify-content-between align-items-center mb-4">
                        <div className="field">
                            <h2 className="text-xl font-bold m-0">{timesheetData?.userName
                                ?.toLowerCase()
                                ?.replace(/\b\w/g, (char) => char?.toUpperCase())}</h2>
                            <p className="text-sm text-500 m-0 mt-1">
                                {timesheetData?.timesheetType} Timesheet ({timesheetData?.weekRange})
                            </p>
                        </div>
                        <div className="md:flex gap-2">
                        {timesheetData?.blobPath && (
                            <Button
                                icon="pi pi-download"
                                id="download_btn"
                                className="download_btn"
                                onClick={handleDownload}
                                tooltip="Download Timesheet Document"
                                tooltipOptions={{ position: "top" }}
                                disabled={!timesheetData?.blobPath || submitting}
                            />
                    )}
                            {!isUserViewingOwnTimesheet && timesheetData?.status?.toLowerCase() === 'pending' ? (
                                <>
                                {!isHROrAccounts &&
                                    <Button
                                        label="Approve"
                                        icon="pi pi-check"
                                        className="p-button-success success_btn mr-1"
                                        onClick={handleApprove}
                                        disabled={submitting}
                                    />
                                }
                                    <Button
                                        label="Reject"
                                        icon="pi pi-times"
                                        className="p-button-danger reject_btn"
                                        onClick={() => setShowRejectDialog(true)}
                                        disabled={submitting}
                                    />
                                </>
                            ) : (
                                !isUserViewingOwnTimesheet && ( 
                                    <div className="flex align-items-center">
                                    <span className={`text-${timesheetData?.status?.toLowerCase() === 'approved' ? 'success' : 'danger'} font-bold mr-2`}>
                                        {timesheetData?.status?.toLowerCase() === 'approved' 
                                            ? "This timesheet has been approved"
                                            : "This timesheet has been rejected"}
                                    </span>
                                </div>
                                )
                            )}
                        </div>
                    </div>

                    <div className="timesheet-details mb-4">
                        <div className="grid">
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label>Total Hours</label>
                                    <InputText value={timesheetData?.totalHours || ''} disabled className="w-full" />
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label>Status</label>
                                    <InputText value={timesheetData?.status || ''} disabled className="w-full" />
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label>Period</label>
                                    <InputText value={`${formatDateForDisplay(timesheetData?.startDate)} - ${formatDateForDisplay(timesheetData?.endDate)}`} disabled className="w-full" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <FormikProvider value={formik}>
                        <form onSubmit={formik.handleSubmit} className="timesheet-form">
                            <FieldArray
                                name="entries"
                                render={() => (
                                    <div className="timesheet-entries">
                                        <DataTable
                                            value={formik.values.entries}
                                            responsiveLayout="scroll"
                                            showGridlines
                                            className="timesheet-table"
                                            tableStyle={{ width: "100%" }}
                                            breakpoint="960px"
                                        >
                                            <Column
                                                header="Date"
                                                body={(rowData) => (
                                                    <div className="date-cell">
                                                        {formatDateForDisplay(rowData.date)}
                                                    </div>
                                                )}
                                                style={{ width: "15%" }}
                                            />
                                            <Column
                                                header="Project"
                                                style={{ width: "28%" }}
                                                body={(rowData) => (
                                                    <div className="projects-container">
                                                        {rowData?.projects?.map((project, idx) => (
                                                            <div key={idx} className="project-row" style={{ height: '43px' }}>
                                                                <InputText
                                                                    value={project?.projectName || ''}
                                                                    disabled
                                                                    className="w-full"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            />
                                            <Column
                                                header="Hours"
                                                style={{ width: "10%" }} // Reduced width
                                                body={(rowData) => (
                                                    <div className="hours-container">
                                                        {rowData?.projects?.map((project, idx) => (
                                                            <div key={idx} className="project-row1 pb-2 border-none" style={{ height: '43px' }}>
                                                                <InputNumber
                                                                    value={project?.hours}
                                                                    disabled
                                                                    mode="decimal"
                                                                    minFractionDigits={1}
                                                                    maxFractionDigits={1}
                                                                    // className="w-full"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            />
                                            <Column
                                                header="Comments"
                                                style={{ width: "28%" }}
                                                body={(rowData) => (
                                                    <div className="comments-container">
                                                        {rowData?.projects?.map((project, idx) => (
                                                            <div key={idx} className="comment-row" style={{ height: '43px' }}>
                                                                <InputText
                                                                    value={project?.comments || ''}
                                                                    disabled
                                                                    className="w-full"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            />
                                        </DataTable>
                                    </div>
                                )}
                            />
                        </form>
                    </FormikProvider>

                    <Dialog
                        header="Reject Timesheet"
                        visible={showRejectDialog}
                        onHide={() => setShowRejectDialog(false)}
                        modal
                        className="p-fluid adminRejectDialog"
                        footer={
                            <div>
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times"
                                    className="p-button-text"
                                    onClick={() => setShowRejectDialog(false)}
                                    disabled={submitting}
                                />
                                <Button
                                    label="Reject"
                                    icon="pi pi-check"
                                    className="p-button-danger"
                                    onClick={handleReject}
                                    disabled={submitting}
                                />
                            </div>
                        }
                    >
                        <div className="field">
                            <label htmlFor="reason">Reason for Rejection</label>
                            <InputTextarea
                                id="reason"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={3}
                                autoFocus
                            />
                        </div>
                    </Dialog>
                </div>
            )}
        </div>
    );
};

export default ApprovalTimeSheet;